import { DateTime } from 'luxon'

import type { InsightRevenueDateTimeRange, InsightRevenuePeriod } from '@/features/site/types/revenue'

function getMinAndMaxDate(revenuePeriods: InsightRevenuePeriod[]): {
  minDate: DateTime | null
  maxDate: DateTime | null
} {
  const minDate = revenuePeriods.reduce(
    (acc, period) => (acc === null || period.earliestCalculated < acc ? period.earliestCalculated : acc),
    null as DateTime | null,
  )
  const maxDate = revenuePeriods.reduce(
    (acc, period) => (acc === null || period.latestCalculated > acc ? period.latestCalculated : acc),
    null as DateTime | null,
  )

  return { minDate, maxDate }
}

/**
 * Get the full available revenue date time range based on the values coming from the periods endpoint.
 *
 * The periods endpoint reports `latestCalculated` as the timestamp of the newest stored revenue row, which lies
 * inside the newest hourly bucket the revenues endpoint can return, while that endpoint treats the requested end
 * time as exclusive. The available range therefore ends at the end of that hour, so a request capped at this
 * limit still includes the newest hour of revenue (ACI-321).
 *
 * If there is an error getting the revenue periods, the limits are set from the beginning of the year to the current date.
 *
 * This is used to constrain the date picker bounds in the UI so users can select any date within the available data.
 */
export function getLimitRevenueDateTimeRange(
  timezone: string,
  revenuePeriods?: InsightRevenuePeriod[],
): InsightRevenueDateTimeRange {
  const { minDate, maxDate } = getMinAndMaxDate(revenuePeriods ?? [])

  return {
    startTime: minDate ?? DateTime.now().setZone(timezone).startOf('year'),
    endTime: maxDate?.endOf('hour') ?? DateTime.now().setZone(timezone),
  }
}
